// src/lib/pantalla.ts
//
// «En qué pantalla estaba la persona» cuando le habló al agente. Viaja en CADA
// petición del chat a Orión como el campo opcional de primer nivel `pantalla`
// (contrato «evidencia automática al crear tickets», v1). Si la persona levanta
// un ticket en esa conversación, Orión lo adjunta tal cual a
// `detection_signal.evidencia.pantalla` — así el análisis sabe que el problema
// vive en el Dashboard (ruta /m/…) y no adivina por palabras (TKT-00217).
//
// Es inofensivo si Orión lo ignora. NUNCA lleva secretos: la ruta pierde los
// parámetros de query sensibles (token, code, state, key, …).

export interface Pantalla {
  ruta: string;
  titulo: string;
  modo: 'pwa' | 'navegador';
  dispositivo: 'ios' | 'android' | 'desktop';
  user_agent: string;
  viewport: string;
  version_app?: string;
}

// Parámetros que pueden traer credenciales o datos de un flujo OAuth.
const PARAMS_SENSIBLES = /^(token|access_token|refresh_token|id_token|code|state|key|api_key|apikey|secret|password|pass|pwd|auth|authorization|jwt|session|sig|signature|otp)$/i;
const PARAM_SOSPECHOSO = /(token|secret|password|api[_-]?key|jwt)/i;

/** pathname + search sin parámetros sensibles (ni el hash). */
export function rutaSegura(pathname: string, search: string): string {
  let qs = '';
  try {
    const params = new URLSearchParams(search);
    for (const k of Array.from(params.keys())) {
      if (PARAMS_SENSIBLES.test(k) || PARAM_SOSPECHOSO.test(k)) params.delete(k);
    }
    const s = params.toString();
    qs = s ? `?${s}` : '';
  } catch {
    qs = '';
  }
  return `${pathname}${qs}`;
}

export function detectarDispositivo(ua: string, maxTouchPoints = 0): Pantalla['dispositivo'] {
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  // iPadOS se presenta como Mac de escritorio; lo delata la pantalla táctil.
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  return 'desktop';
}

function esPwa(): boolean {
  try {
    if (document.documentElement.dataset.app === 'instalada') return true;
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
    return ['(display-mode: standalone)', '(display-mode: fullscreen)', '(display-mode: minimal-ui)'].some(
      (q) => window.matchMedia?.(q).matches,
    );
  } catch {
    return false;
  }
}

/** Versión del build (vite.config.ts → `__APP_VERSION__`); vacía si no hay. */
function versionApp(): string | undefined {
  try {
    const v = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__.trim() : '';
    return v || undefined;
  } catch {
    return undefined;
  }
}

/** Foto de la pantalla actual. `undefined` si algo falla: nunca bloquea el envío. */
export function capturarPantalla(): Pantalla | undefined {
  try {
    const ua = navigator.userAgent || '';
    const p: Pantalla = {
      ruta: rutaSegura(window.location.pathname, window.location.search).slice(0, 300),
      titulo: (document.title || '').slice(0, 200),
      modo: esPwa() ? 'pwa' : 'navegador',
      dispositivo: detectarDispositivo(ua, navigator.maxTouchPoints || 0),
      user_agent: ua.slice(0, 400),
      viewport: `${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)}`,
    };
    const v = versionApp();
    if (v) p.version_app = v.slice(0, 60);
    return p;
  } catch {
    return undefined;
  }
}
